import axios from 'axios';
import queryString from 'query-string';
import { isNil } from 'ramda';

import { PaginationMetaOptionsType } from 'components/shared/types';
import { showSnackbar } from 'hooks/useSnackbar';
import axiosApi from 'utils/axios';
import { axiosGetAutoCancelDecorator } from 'utils/axiosGetAutoCancelDecorator';

import { SNACKBAR_SKIN } from '../components/shared/Snackbar';
import { decamelize } from '../utils/humps';
import { getAccPath } from './auth';

export type PartnerAgent = {
  id: number;
  companyName: string;
  email: string;
  consultantFirstName: string | null;
  consultantLastName: string | null;
  logo: {
    url: string;
    mimeType: string;
    filename: string;
    id?: string;
  } | null;
  phone: string | null;
  primaryColor: string | null;
};

type GetPartnerAgentsResponse = {
  data: PartnerAgent[];
  meta: PaginationMetaOptionsType;
};

const decoratedAxiosGet = axiosGetAutoCancelDecorator();

export const getPartnerAgents = async (search: string, page?: number, limit?: number) => {
  const query = queryString.stringify({ q: search, page, limit }, { skipNull: true, skipEmptyString: true });

  return decoratedAxiosGet(`${getAccPath()}/contacts/partners?${query}`)
    .then(({ data }) => data as GetPartnerAgentsResponse)
    .catch((err) => {
      if (!(err instanceof axios.Cancel)) {
        showSnackbar({
          uniqueId: 'getPartnerAgents',
          text: 'Oops... Problems with getting contact partners data',
          type: SNACKBAR_SKIN.ERROR,
        });
      }

      throw err;
    });
};

export const getPartnerAgent = async (id: number | string) => {
  return axiosApi
    .get(`${getAccPath()}/contacts/partners/${id}`)
    .then(({ data }) => data as PartnerAgent)
    .catch((err) => {
      showSnackbar({
        uniqueId: 'getPartnerAgent',
        text: 'Oops... Problems with getting partner data',
        type: SNACKBAR_SKIN.ERROR,
      });

      throw err;
    });
};

export const submitPartnerAgent = async (values: PartnerAgent, needRemoveLogo: boolean) => {
  const data = await getPartnerAgentFormData(values, needRemoveLogo);

  return axiosApi
    .request({
      url: `${getAccPath()}/contacts/partners${values.id ? '/' + values.id : ''}`,
      method: values.id ? 'PATCH' : 'POST',
      data,
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then(({ data }) => data as PartnerAgent)
    .catch((err) => {
      showSnackbar({
        uniqueId: 'submitPartnerAgent',
        text: 'Oops... Problems with submitting partner agent data',
        type: SNACKBAR_SKIN.ERROR,
      });

      throw err;
    });
};

export const deletePartnerAgent = async (id: number) => {
  return axiosApi
    .delete(`${getAccPath()}/contacts/partners/${id}`)
    .then(({ data }) => data as PartnerAgent)
    .catch((err) => {
      showSnackbar({
        uniqueId: 'deletePartnerAgent',
        text: 'Oops... Problems with deleting partner agent',
        type: SNACKBAR_SKIN.ERROR,
      });

      throw err;
    });
};

export const checkPartnerIsUsed = async (id: number) => {
  return axiosApi
    .get(`${getAccPath()}/contacts/partners/${id}/check_use`)
    .then(({ data }) => data?.used as boolean)
    .catch((err) => {
      showSnackbar({
        uniqueId: 'checkPartnerIsUsed',
        text: 'Oops... Problems with checking partner in use',
        type: SNACKBAR_SKIN.ERROR,
      });

      throw err;
    });
};

export const getPartnerAgentFormData = async (values: PartnerAgent, needRemoveLogo: boolean) => {
  const bodyFormData = new FormData();
  const keys = Object.keys(values) as unknown as keyof PartnerAgent;

  for (const key of keys) {
    if (key !== 'logo') {
      // @ts-expect-error
      bodyFormData.append(decamelize(key), isNil(values[key]) ? '' : values[key]);
    }
  }

  // if user removed existing logo
  if (needRemoveLogo) {
    bodyFormData.append('logo', '');
    // if user chosen logo from computer
  } else if (!isNil(values.logo) && isNil(values?.logo?.id)) {
    const blob = await fetch(values.logo.url).then((r) => r.blob());
    bodyFormData.append('logo', new File([blob], 'logo'));
  }

  return bodyFormData;
};
